import { MapTrifold, Barbell } from "@phosphor-icons/react";
import { SectionEyebrow, SectionTitle, FadeUp } from "./_shared";
import HudCorners from "./_partials/HudCorners";

const APPS = [
  {
    icon: Barbell,
    code: "APP.01",
    badge: "Live · Member Portal",
    imageBadge: "Body Scan · Live Capturing",
    title: "Optimization Programs",
    href: "https://iwmc-body-app.emergent.host",
    image: "https://iwmc-body-app.emergent.host/static/media/hero-couple.ca5e86df1fcdc2a9ef2d.webp",
    imageAlt:
      "Integrated Wellness members with full-body scan overlays — the Optimization Programs portal",
    body: "Nine medically supervised, data-driven optimization programs — body transformation, metabolic, gut, longevity, performance, cognitive, hormonal, sleep and life optimization. Body scans every two weeks, wearable integration, and human coaching.",
    stats: ["9 Programs", "Medically Supervised", "Nationwide"],
    cta: "Enter the Programs",
  },
  {
    icon: MapTrifold,
    code: "APP.02",
    badge: "Live · 12 States + DC",
    imageBadge: "ME → VA · Coverage Map",
    title: "Integrated Wellness Community",
    href: "https://community-health-map-1.emergent.host",
    image:
      "https://static.prod-images.emergentagent.com/jobs/d50f8085-4875-4de7-b3cb-affb13e5c279/images/d6143e27464e8480ff615d253e5a8521b003a6abba0a09c1686fde62e9b1b619.jpeg",
    imageAlt:
      "Eastern Seaboard coverage map from Maine to Virginia — the Integrated Wellness Community directory",
    body: "Discover 4,000+ goal-rated wellness venues — gyms, restaurants, recovery studios — graded across 10 clinical domains, from Maine to Virginia. Find training partners, join local events, and take on community challenges.",
    stats: ["4,368 Listings", "12 States + DC", "10 CPI Domains"],
    cta: "Explore the Community",
  },
];

function AppCard({ app, index }) {
  const { icon: Icon, code, badge, imageBadge, title, href, image, imageAlt, body, stats, cta } = app;
  return (
    <FadeUp delay={index * 0.08}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`ecosystem-app-${code.toLowerCase().replace(".", "-")}`}
        className="iw-track-card group relative flex flex-col h-full overflow-hidden"
      >
        {/* Visual header — pulled from the live app */}
        <div className="iw-scan-sweep relative aspect-[16/9] overflow-hidden bg-foreground">
          <img
            src={image}
            alt={imageAlt}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
            style={{ filter: "brightness(1.05) contrast(1.05) saturate(1.08)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/60 via-transparent to-[#11296B]/15 pointer-events-none" />
          <HudCorners offset={10} size={10} />
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-white/10 backdrop-blur-md border border-[color:var(--iw-blue-bright)]/60">
            <span className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-white font-semibold">
              {imageBadge}
            </span>
          </div>
          <span className="absolute bottom-3 right-3 font-mono text-[0.6rem] tracking-[0.22em] text-white/85">
            {code}
          </span>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-7 md:p-8">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
              </span>
              <span className="font-mono text-[0.58rem] uppercase tracking-[0.2em] text-primary font-semibold">
                {badge}
              </span>
            </div>
            <Icon size={26} weight="duotone" className="text-accent" />
          </div>
          <h3 className="mt-4 font-display text-xl md:text-2xl font-bold leading-tight text-primary">
            {title}
          </h3>
          <p className="mt-3 text-base text-foreground/75 leading-relaxed flex-1">{body}</p>

          {/* Stat chips */}
          <div className="mt-5 flex flex-wrap gap-2">
            {stats.map((s) => (
              <span
                key={s}
                className="border border-border bg-background py-1.5 px-3 font-mono text-[0.58rem] uppercase tracking-[0.14em] text-primary font-semibold"
              >
                {s}
              </span>
            ))}
          </div>

          <span className="iw-btn-red mt-6 inline-flex items-center gap-2 px-6 py-3 text-[0.65rem] w-fit group-hover:gap-3 transition-all">
            {cta} <span aria-hidden>→</span>
          </span>
        </div>
      </a>
    </FadeUp>
  );
}

export default function EcosystemApps() {
  return (
    <section
      id="platform-apps"
      data-testid="ecosystem-apps-section"
      className="relative py-20 md:py-28 bg-background border-y border-border"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-30" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Live Platforms</SectionEyebrow>
          <SectionTitle className="mt-4 text-[clamp(1.9rem,4.5vw,3.25rem)]">
            The Integrated Wellness <em className="not-italic text-accent">ecosystem</em>.
          </SectionTitle>
          <p className="mt-6 text-lg md:text-xl text-foreground/75 leading-relaxed">
            Two live companion platforms — your optimization programs and the community
            around them.
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {APPS.map((app, i) => (
            <AppCard key={app.code} app={app} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
